import React from 'react';
import { Shield, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReligionType } from '@/hooks/useReligionSettings';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface ReligionConfirmSheetProps {
  isOpen: boolean;
  religion: ReligionType;
  onConfirm: () => void;
  onCancel: () => void;
}

const getReligionIcon = (religion: ReligionType) => {
  switch (religion) {
    case 'jewish': return '✡️';
    case 'christian': return '✝️';
    case 'muslim': return '☪️';
    case 'hindu': return '🕉️';
    default: return '🙏';
  }
};

const getReligionName = (religion: ReligionType) => {
  switch (religion) {
    case 'jewish': return 'Judaism';
    case 'christian': return 'Christian';
    case 'muslim': return 'Muslim';
    case 'hindu': return 'Hindu';
    default: return 'Other Faith';
  }
};

const ReligionConfirmSheet: React.FC<ReligionConfirmSheetProps> = ({
  isOpen,
  religion,
  onConfirm,
  onCancel
}) => {
  const religionName = getReligionName(religion);
  const religionIcon = getReligionIcon(religion);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <SheetContent side="bottom" className="rounded-t-3xl px-6 pb-8 pt-4">
        <SheetHeader className="mb-6">
          <div className="w-12 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-4" />
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="text-4xl">{religionIcon}</span>
          </div>
          <SheetTitle className="text-2xl font-bold text-center">
            {religionName} Mode
          </SheetTitle>
          <p className="text-center text-muted-foreground mt-2">
            This space is for respectful, faith-aligned connections.
          </p>
        </SheetHeader>

        {/* Features */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-foreground">Matches only within {religionName}</span>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-foreground">Your selection is private</span>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-foreground">You can change anytime</span>
          </div>
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <Button
            onClick={onConfirm}
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full shadow-lg"
          >
            Continue
          </Button>
          <Button
            onClick={onCancel}
            variant="ghost"
            className="w-full h-12 text-muted-foreground"
          >
            Cancel
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ReligionConfirmSheet;
